import { SocketClient } from '@/shared/realtime';
import type { SocketClientOptions } from '@/shared/realtime';
import {
  CHAT_SOCKET_CLIENT_EVENT,
  WS_PATH,
  type ChatSocketClientEventName,
  type ChatSocketClientRequestMap,
  type ChatSocketServerEnvelope,
  type ChatSocketServerEventMap,
  type ChatSocketServerEventName,
} from './chatSocketTypes';

export type ChatSocketClientOptions = Omit<SocketClientOptions, 'path'> & {
  path?: string;
};

export class ChatSocketClient {
  private readonly socket: SocketClient;

  constructor(options: ChatSocketClientOptions) {
    this.socket = new SocketClient({
      ...options,
      path: options.path ?? WS_PATH,
    });
  }

  connect() {
    return this.socket.connect();
  }

  disconnect() {
    this.socket.disconnect();
  }

  reconnectWithFreshToken() {
    return this.socket.reconnectWithFreshToken();
  }

  isConnected() {
    return this.socket.isConnected();
  }

  onConnect(handler: () => void) {
    return this.socket.onConnect(handler);
  }

  onDisconnect(handler: (reason: string) => void) {
    return this.socket.onDisconnect(handler);
  }

  onChatEvent<E extends ChatSocketServerEventName>(
    event: E,
    handler: (envelope: ChatSocketServerEnvelope<E>) => void,
  ): () => void {
    return this.socket.on(event, handler as (...args: unknown[]) => void);
  }

  subscribeChatList(chatIds: number[]): Promise<void> {
    return this.emitChatCommand(CHAT_SOCKET_CLIENT_EVENT.ChatListSubscribe, { chatIds });
  }

  subscribeChatMessages(chatId: number): Promise<void> {
    return this.emitChatCommand(CHAT_SOCKET_CLIENT_EVENT.ChatMessagesSubscribe, { chatId });
  }

  chatsUnsubscribe(): Promise<void> {
    return this.emitChatCommand(CHAT_SOCKET_CLIENT_EVENT.ChatsUnsubscribe);
  }

  subscribeNotifications(): Promise<void> {
    return this.socket.emitWithAck('notifications:subscribe');
  }

  unsubscribeNotifications(): Promise<void> {
    return this.socket.emitWithAck('notifications:unsubscribe');
  }

  onNotificationEvent(event: string, handler: (...args: unknown[]) => void): () => void {
    return this.socket.on(event, handler);
  }

  private emitChatCommand<E extends ChatSocketClientEventName>(
    event: E,
    ...args: ChatSocketClientRequestMap[E] extends void
      ? []
      : [payload: ChatSocketClientRequestMap[E]]
  ): Promise<void> {
    const payload = args[0];
    return this.socket.emitWithAck(event, payload);
  }
}

export type { ChatSocketServerEventMap, ChatSocketServerEventName };
